import { cn } from "@/utils/cn";
import { Tooltip } from "./Tooltip";
import type { ReplacementBadgeState } from "@/hooks/useReplacementBadge";

type RowReplaceBtnProps = {
  compoundId: string;
  badge: ReplacementBadgeState;
  expanded: boolean;
  onClick: (id: string) => void;
  /** Sublabel text for the tooltip when the difficulties dot is on (e.g.
   *  "No supplier registered" or "Danger hazard"). When the badge is in
   *  the "both" state this becomes the tooltip's sublabel; in the pure
   *  "difficulties" state it gets folded into the primary label. */
  difficultyText?: string;
};

const SwapIcon = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 9h12l-3-3" />
    <path d="M20 15H8l3 3" />
  </svg>
);

// Orange = better-loss available; grey = procurement/safety difficulty.
// Both dots use a var(--panel)-colored border so they read cleanly on any
// row background and so the overlap reads as two distinct circles.
const ORANGE = "#d97706";
const GREY = "var(--ink-4)";

function tooltipFor(
  badge: ReplacementBadgeState,
  expanded: boolean,
  difficultyText: string | undefined,
): { label: string | null; sublabel?: string } {
  if (expanded) return { label: "Hide replacement options" };
  switch (badge.kind) {
    case "disabled":
      return { label: null };
    case "default":
      return { label: "Find replacement" };
    case "better-loss":
      return { label: "Replacement available with lower loss" };
    case "difficulties":
      return {
        label: difficultyText
          ? `Replacement available · ${difficultyText}`
          : "Replacement available",
      };
    case "better-loss-and-difficulties":
      return {
        label: "Replacement available with lower loss",
        sublabel: difficultyText,
      };
  }
}

export function RowReplaceBtn({
  compoundId,
  badge,
  expanded,
  onClick,
  difficultyText,
}: RowReplaceBtnProps) {
  const disabled = badge.kind === "disabled";
  const showOrange =
    badge.kind === "better-loss" || badge.kind === "better-loss-and-difficulties";
  const showGrey =
    badge.kind === "difficulties" || badge.kind === "better-loss-and-difficulties";
  const both = showOrange && showGrey;

  const tip = tooltipFor(badge, expanded, difficultyText);

  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick(compoundId);
      }}
      className={cn(
        "row-replace-btn",
        expanded && "expanded",
        disabled && "disabled",
      )}
    >
      <SwapIcon />
      {/* Grey dot renders first (lower z) so the orange dot's panel-colored
          border isn't clipped where the two circles overlap in the "both"
          state. In the pure-difficulties state, the grey dot sits at the
          default top-right anchor (no offset class). */}
      {showGrey && (
        <span
          className={cn("row-replace-dot", both && "row-replace-dot--secondary")}
          style={{ background: GREY }}
        />
      )}
      {showOrange && (
        <span className="row-replace-dot" style={{ background: ORANGE }} />
      )}
    </button>
  );

  if (tip.label === null) return button;
  return (
    <Tooltip label={tip.label} sublabel={tip.sublabel}>
      {button}
    </Tooltip>
  );
}
